import { Config } from 'config';
import { find, invokeMap, assign } from 'lodash';

import { OwnedObject, invalidateOwner, isArray,
         strokeWireframe } from 'gear';

import { Point, Matrix, Bounds, Frame, Boundary, Polyline } from '../../geometry';

import { Xml } from '../../util';
import { Color, Brush, Pen } from '../../style';

import { Region, mapCurvesToRegions } from './region';

const _Math = Math,
      _max  = _Math.max;

function recolorized( thing, config ) {
  const colorMap = config ? config.colorMap : null;
  if( colorMap ) {
    return colorMap.recolorized(thing);
  }
  else {
    return thing;
  }
}

// Used in hitTest
function prepateHitTestPolygon(hull,transform) {
  hull.push(hull[0]);
  if( transform ) {
    for( let k = 0,kEnd=hull.length ; k < kEnd ; ++k) {
      hull[k] = transform.apply(hull[k]);
    }
  }
  return hull;
}

export const Polyregion = OwnedObject.extend({ typeName: 'Polyregion', __debugProps: true,

  Properties: {
    regions      : { type: 'Region[]',  def: [], manualParsing: true     , onChange: function(){ this.invalidate(); } },
    pen          : { type: 'Pen'      , def:   Pen.empty, onChange: invalidateOwner },
    brush        : { type: 'Brush'    , def: Brush.empty, onChange: invalidateOwner },
    fillMode     : { type: 'string'   , def: 'CadX', set: function(v){ this.setFillMode(v); } },
    powerClipId  : { type: 'string'   , def: null                        , onChange: invalidateOwner },
    renderFrame  : { type: 'Frame'    , def: null            , onChange: invalidateOwner },
    renderMatrix : { type: 'Matrix22' , def: null            , onChange: invalidateOwner },
    tags         : { type: 'string[]' , def: [], dropDef: true, onChange: invalidateOwner },

    // Used in output figure polyregions, to reference PowerClipContent in the input figure
    // A PowerClipLink has an extra matrix so we are able to transform the content without cloning it
    powerClipLink: { type: 'PowerClipLink', def: null, manualParsing: true }
  },

  setFillMode( v ) {
    if( this._fillMode !== v ) {
      if( v !== 'CadX' && this.hasHoles()) {
        // flatten all holes into regions, soup of curves
        this._regions = mapCurvesToRegions(this.enumCurves());
      }

      this._fillMode = v;
      this.invalidateOwner();
      return this;
    }
  },

  enumCurves() {
    var allCurves = [];
    var regions = this._regions;
    for(var k=0,kEnd=regions.length; k < kEnd; ++k ) {
      var curves = regions[k].curves;
      for(var c=0,cEnd=curves.length; c < cEnd; ++c ) {
        allCurves.push(curves[c]);
      }
    }
    return allCurves;
  },

  hasHoles() {
    var regions = this._regions;
    for(var k=0,kEnd=regions.length; k < kEnd; ++k ) {
      var curves = regions[k].curves;
      if (curves.length>1)
        return true;
    }
    return false;
  },

  invalidateOwner,

  get windingRule() {
    return this.fillMode === 'Winding' && !this.hasHoles() ? 'nonzero' : 'evenodd';
  },
  set windingRule( windingRule_ ) {
    this.fillMode = ( windingRule_ === 'nonzero' ? 'Winding' : 'Alternate' );
  },

  init( ca, cb ) {
    // Allow Polyregion(regions,config) for backward compatibility
    this._bounds = null;
    if( isArray(ca) ) {
      this.initProperties(cb);
      this.callParent(cb);
      this._regions.set(ca);
    }
    else {
      this.callParent(ca);
    }

    this.renderMatrixAndRenderFrameFixup();
  },

  renderMatrixAndRenderFrameFixup() {
    if(!(this._brush && (this._brush._type == "LinearGradientBrush" || this._brush._type == "RadialGradientBrush" || this._brush._type == "TextureBrush" || this._brush._type == "PatternBrush"))) {
      this._renderFrame = null
      this._renderMatrix = null
    }
  },

  transform( t ) {

    // After calling transform, both the renderFrame and renderMatrix needs to be maintained
    // Do not change the order, it is important to do this before actually transforming the points
    let renderFrame = this._renderFrame;
    if( ! renderFrame ) {
      renderFrame = this._renderFrame = this.initialRenderFrame();
    }
    renderFrame.transform(t);

    let renderMatrix = this._renderMatrix;
    if( ! renderMatrix ) {
      renderMatrix = new Matrix();
    }
    this._renderMatrix = Matrix.m(t,renderMatrix);

    // Now transform the points
    invokeMap(this._regions,'transform',t);

    // [TODO][PC] Explore: Should we transform PC Links inside here instead
    //            of doing it offline?

    this.invalidate();
    return this;
  },

  invalidate() {
    this.outdate();
    this.callParent();
  },
  outdate() {
    this._bounds = null;
  },

  buildXml( node ) {
    Xml.addObject(node,this,'Polyregion');
  },
  setupXml( node ) {
    Xml.setupProperties(node,this);
    const regions = this._regions;
    //_.forEach(regions,function(r) {
    for(let k=0,kEnd=regions.length;k<kEnd;++k) {
      regions[k].buildXml(node);
    }//);
  },

  get center() {
    return this.bounds.center;
  },

  get bounds() {
    return this._bounds || ( this._bounds = Bounds.ofThings(this._regions) );
  },

  boundsAfter( transform ) {
    return Bounds.ofThingsAfter(this._regions,transform) ;
  },
  initialRenderFrame() {
    return Frame.fromBounds(this.bounds);
  },

  // This function returns true if the query point falls inside the
  // Polyregion. The possible options are:
  // - regionFrames: tells the function to check only the bounds of the regions
  // of the polyregion. This option is used in text selection.
  // - distance: don't check the stroke when the pen width is smaller than this
  // value.
  hitTest( point, transform, options = {} ) {

    // There are two things to check, the outline and the interior. If the brush
    // is transparent, we don't check the interior. If the pen is thin, we don't
    // check the outline.
    var checkOutline=true,
        checkInterior=true;
    const brush = this.rwBrush();
    const pen = this.rwPen();
    const scaledPenWidth_2=pen.width*transform.scaleFactor()/2;
    var checkWidth=(options.distance===undefined?0:options.distance);

    if( Config.selectUnderTransparentFigures && ( brush._empty || (brush instanceof Brush && brush.color.alpha===0) ) ){
      checkInterior=false;
      checkWidth=_max(checkWidth,scaledPenWidth_2);
    }else{
      if( pen instanceof Pen && scaledPenWidth_2>=checkWidth ){
        checkWidth=scaledPenWidth_2;
      }else{
        checkOutline=false;
      }
    }
    const regions = this._regions;

    if( options.regionFrames ) {
      // Simplified hit test used
      for( let ir = 0, irEnd=regions.length; ir < irEnd; ++ir ) {
        const region = regions[ir];
        const hull =
          prepateHitTestPolygon(region.curves[0].controlPoints(),transform);
        if( Bounds.ofPoints(hull).inflate(checkWidth,checkWidth).contains(point) ) {
          return true;
        }
      }
      return false;
    }

    // We compute the squared pen width we will use to perform the hit test (we
    // want to avoid square roots).
    var squaredCheckWidth=checkWidth*checkWidth;

    if(this.hasHoles()) {
      // Iterate over all the regions of the polyregion.
      for( let ir = 0, irEnd=regions.length; ir < irEnd; ++ir ) {
        const region = regions[ir] ;
        // The first curve is the outer one.
        const boundary = region.curves[0];
        // Check first if the query point is inside the bounds of the outer
        // curve.
        let outerCurve=
          prepateHitTestPolygon(boundary.controlPoints(),transform);
        let inside =
          (checkInterior && Point.isInsidePolygon(point,outerCurve)) ||
          (checkOutline &&
           Point.isNearPolygon(point,outerCurve,squaredCheckWidth));
        if( inside ) { // Check the holes if the point is inside the bounds.
          const holes = region.curves.slice(1);
          // Iterate over the holes of the polyregion until finding a hole
          // whose bounds contain the query point. If there is no such
          // hole, then the query point is inside the region.
          for(let ih = 0; inside && ih < holes.length; ih++ ) {
            let holeHull=
              prepateHitTestPolygon(holes[ih].controlPoints(),transform);
            // A problem with the following test is that it is performed
            // using the holes control points. From some applications, this
            // is OK because the curve is contained in the convex hull of
            // its control points. But this can give false positives.
            inside =
              ! ((checkInterior && Point.isInsidePolygon(point,holeHull)) ||
                 (checkOutline &&
                  Point.isNearPolygon(point,holeHull,squaredCheckWidth)));
          }
          // At this point, inside is true if the query point is inside the
          // boundary of a region and outside all the holes.
        }
        if( inside ) {
          return true;
        }
        // If inside is false, we keep iterating the regions to determine if
        // the query point falls inside one of them.
      }
      // At this point, we know the query point is outside all the regions
      // of the polyregion. We finally test the distance to the segments, in
      // order to see if the query point falls into the pen width.
    }
    else {
      // Create one big hull to test. This hull can be non-convex.
      let hull = [];
      // Iterate over all the regions.
      for( let ir = 0, irEnd=regions.length; ir < irEnd; ++ir ) {
        const region = regions[ir] ;
        const curves = region.curves;
        // Iterate over all the curves of the region.
        for(let ic = 0; ic < curves.length; ic++ ) {
          // polygon must contain the control points (which are its
          // vertices) and, to respect the format of Cx.Polygon, the last
          // point is a copy of the first one.
          const polygon = prepateHitTestPolygon(curves[ic].controlPoints(),
                                                transform);
          // With the new polygon, we can test whether the query point is within
          // a distance to the region edges. In case it is, we return true.
          if(checkOutline &&
             Point.isNearPolygon(point,polygon,squaredCheckWidth)){
            return true;
          }
          // Now, the point at (0,0) is added to the list of vertices. This
          // does the trick of letting all the regions be enclosed in a
          // continuous hull. If we simply concatenate vertices, we can
          // generate a region which is bigger than the current hull.
          hull.push(transform.apply(new Point(0,0)));
          hull = hull.concat(polygon);
        }
      }

      // Close the hull, back to (0,0)
      hull.push(transform.apply(new Point(0,0)));

      // Finally, we perform the test to see if the query point is inside the
      // curves. The closeness to the polygons was already tested.
      if( this._fillMode === 'Alternate' ) {
        // Return true if the point is inside the hull of the control
        // points.
        return (checkInterior && Point.isInsidePolygon(point,hull,false));
      } else { // FillMode: Winding
        // If the winding number of the query point is not zero, the hit test
        // returns true.
        // TODO: The latter test must be replaced by the distance to the curve
        // and not the distance to the hull.
        return (checkInterior && (Point.windNumber(point,hull) !== 0));
      }
    }
    return false;
  },

  toSVG() {
    let svgCurve = '';
    const regions = this._regions;
    //_.forEach(regions,function(r) {
    for(let k=0,kEnd=regions.length;k<kEnd;++k) {
      svgCurve += regions[k].toSVG();
    }//);
    return svgCurve ;
  },

  getFirstPoint() {
    return this._regions[0].getFirstPoint();
  },

  drawFill( context, transform, config, matrix, brush ) {

    if( matrix === undefined ) {
      matrix = this.globalMatrix_(transform);
    }
    if( brush === undefined ) {
      brush = recolorized( this.rwBrush(), config );
    }

    if( ! brush._empty ) {
      
      let frame; // Avoid computing the render frame if it will not be used
      if( brush.needsFrame() ) {
        frame = ( this.renderFrame || this.initialRenderFrame() ).transformed(matrix);
      }    

      const renderMatrix = this._renderMatrix;
      const rm = renderMatrix ? Matrix.m(matrix,renderMatrix) : matrix;

      if (brush.needsClipping()) {
        context.save();
        context.clip(this.windingRule);
        brush.setupContext(context,rm,frame,config.transformDoc);
        context.restore();
      }
      else
      {
        context.save();
        brush.setupContext(context,rm,frame,config.transformDoc);
        context.fill(this.windingRule);
        context.restore();
      }
    }
  },

  drawStroke( context, transform, config, matrix, pen ) {

    if( matrix === undefined ) {
      matrix = this.globalMatrix_(transform);
    }
    if( pen === undefined ) {
      pen = recolorized( this.rwPen(), config );
    }

    if( ! pen._empty ) {

      const pen_type = pen._type;
      if( pen_type === 'MaskPen' ||
          pen_type === 'DisplayPen' ) {
        // This polyregion will be picked up later by the view
        void 0;
      }
      else if( pen_type === 'CutPen' ||
               pen._color._alpha > 0.0 ) {
        context.save();
        pen.setupContext(context,matrix);
        context.stroke();
        context.restore();
      }
    }
  },

  drawShowthroughMask( drawPath, context, transform, config, matrix ) {
    if( matrix === undefined ) {
      matrix = this.globalMatrix_(transform);
    }

    let pen = recolorized( this.rwPen(), config );
    pen = pen.clone();
    let lColors = pen.getColors();
    let lNrColors = lColors.length;
    for(let i=0;i<lNrColors;i++) {
      if( lColors[i].showThrough ) {
          lColors[i].alpha = 1;
          lColors[i].rgb = 'FFFFFF';
        }
        else {
          lColors[i].rgb = '000000';
        }
    }

    const penBehindFill = ! pen._empty &&   pen._behindFill;
    const penAfterFill  = ! pen._empty && ! pen._behindFill;

    let clipped = false;

    drawPath(context,matrix);

    if( penBehindFill && !config.ignorePen ) {
      this.drawStroke(context,transform,config,matrix,pen);
    }

    let brush = recolorized( this.rwBrush(), config );
    if (brush.getType()=='TextureBrush' || brush.getType()=='PatternBrush')
    {
      brush = new Brush({ color: Color.Black() });
    }  
    else
    {
      brush = brush.clone();
      lColors = brush.getColors();
      lNrColors = lColors.length;
      for(let i=0;i<lNrColors;i++) {
        if( lColors[i].showThrough ) {
            lColors[i].alpha = 1;
            lColors[i].rgb = 'FFFFFF';
          }
          else {
            lColors[i].rgb = '000000';
          }
      }  
    }


    this.drawFill(context,transform,config,matrix, brush);

    let pcContent, pcMatrix;

    // PowerClip support, using PowerClipLink for output figures
    // These links directly reference input power clips

    const parent = this._owner;
    if( parent ) {

      const pcLink = this._powerClipLink;
      if( pcLink ) {
        // console.log(pcLink._content._id);
        pcContent = pcLink._content;
        pcMatrix  = pcLink._matrix;
      }
      else {
        const pc_id  = this._powerClipId;
        if( pc_id !== null ) {
          const pccs = parent._powerClipContents;
          pcContent = find( pccs, { _id: pc_id } );
          pcMatrix  = new Matrix();
        }
      }

      if( pcContent ) {
        context.save();
        context.clip(this.windingRule);
        clipped = true;

        // Powerclips needs to be transformed with a different rw transform than
        // the one used for the polyregions paths, they are relative to the pin and not the
        // the figure anchor.
        const pcTransform = Matrix.concat( transform, parent.rwPowerClipTransform(), pcMatrix );
        pcContent.draw(context,pcTransform,config);
        context.restore();
      }
    }

    if( penAfterFill && !config.ignorePen ) {
      if( clipped ) {
        // It seems that even if you do a context.save() once you perform a context.clip()
        // things are screwed for the used path, and it needs to be redrawn
        drawPath(context,matrix);
      }
      this.drawStroke(context,transform,config,matrix,pen);
    }
  },

  drawSolidShowthrough( drawPath, context, transform, config, matrix ) {
    if( matrix === undefined ) {
      matrix = this.globalMatrix_(transform);
    }

    let pen = recolorized( this.rwPen(), config );
    pen = pen.clone();
    let lColors = pen.getColors();
    let lNrColors = lColors.length;
    for(let i=0;i<lNrColors;i++) {
      if( lColors[i].showThrough ) {
          lColors[i].alpha = 1;
          lColors[i].rgb = config.showthroughColor.rgb;
        }
    }

    const penBehindFill = ! pen._empty &&   pen._behindFill;
    const penAfterFill  = ! pen._empty && ! pen._behindFill;

    let clipped = false;

    drawPath(context,matrix);

    if( penBehindFill && !config.ignorePen ) {
      this.drawStroke(context,transform,config,matrix,pen);
    }

    let brush = recolorized( this.rwBrush(), config );
    if (brush.getType()=='TextureBrush' || brush.getType()=='PatternBrush')
    {
      brush = new Brush({ color: Color.Black() });
    }  
    else
    {
      brush = brush.clone();
      lColors = brush.getColors();
      lNrColors = lColors.length;
      for(let i=0;i<lNrColors;i++) {
        if( lColors[i].showThrough ) {
            lColors[i].alpha = 1;
            lColors[i].rgb = config.showthroughColor.rgb;
          }
      }
    }


    this.drawFill(context,transform,config,matrix, brush);

    let pcContent, pcMatrix;

    // PowerClip support, using PowerClipLink for output figures
    // These links directly reference input power clips

    const parent = this._owner;
    if( parent ) {

      const pcLink = this._powerClipLink;
      if( pcLink ) {
        // console.log(pcLink._content._id);
        pcContent = pcLink._content;
        pcMatrix  = pcLink._matrix;
      }
      else {
        const pc_id  = this._powerClipId;
        if( pc_id !== null ) {
          const pccs = parent._powerClipContents;
          pcContent = find( pccs, { _id: pc_id } );
          pcMatrix  = new Matrix();
        }
      }

      if( pcContent ) {
        context.save();
        context.clip(this.windingRule);
        clipped = true;

        // Powerclips needs to be transformed with a different rw transform than
        // the one used for the polyregions paths, they are relative to the pin and not the
        // the figure anchor.
        const pcTransform = Matrix.concat( transform, parent.rwPowerClipTransform(), pcMatrix );
        pcContent.draw(context,pcTransform,config);
        context.restore();
      }
    }

    if( penAfterFill && !config.ignorePen ) {
      if( clipped ) {
        // It seems that even if you do a context.save() once you perform a context.clip()
        // things are screwed for the used path, and it needs to be redrawn
        drawPath(context,matrix);
      }
      this.drawStroke(context,transform,config,matrix,pen);
    }
  },

  drawStrokeAndFill( drawPath, context, transform, config, matrix ) {
    if( matrix === undefined ) {
      matrix = this.globalMatrix_(transform);
    }
    let pen = recolorized( this.rwPen(), config );

    pen = pen.clone();
    let lColors = pen.getColors();
    let lNrColors = lColors.length;
    for(let i=0;i<lNrColors;i++) {
      if( lColors[i].showThrough ) {
          lColors[i].alpha = 0;
        }
    }

    const penBehindFill = ! pen._empty &&   pen._behindFill;
    const penAfterFill  = ! pen._empty && ! pen._behindFill;

    let clipped = false;

    drawPath(context,matrix);

    if( penBehindFill && !config.ignorePen ) {
      this.drawStroke(context,transform,config,matrix,pen);
    }
    
    let brush = recolorized( this.rwBrush(), config );
    if (!(brush.getType()=='TextureBrush' || brush.getType()=='PatternBrush'))
    {
      brush = brush.clone();
      lColors = brush.getColors();
      lNrColors = lColors.length;
      for(let i=0;i<lNrColors;i++) {
        if( lColors[i].showThrough ) {
            lColors[i].alpha = 0;
          }
      }  
    }

    this.drawFill(context,transform,config,matrix, brush);

    let pcContent, pcMatrix;

    // PowerClip support, using PowerClipLink for output figures
    // These links directly reference input power clips

    const parent = this._owner;
    if( parent ) {

      const pcLink = this._powerClipLink;
      if( pcLink ) {
        // console.log(pcLink._content._id);
        pcContent = pcLink._content;
        pcMatrix  = pcLink._matrix;
      }
      else {
        const pc_id  = this._powerClipId;
        if( pc_id !== null ) {
          const pccs = parent._powerClipContents;
          pcContent = find( pccs, { _id: pc_id } );
          pcMatrix  = new Matrix();
        }
      }

      if( pcContent ) {
        context.save();
        context.clip(this.windingRule);
        clipped = true;

        // Powerclips needs to be transformed with a different rw transform than
        // the one used for the polyregions paths, they are relative to the pin and not the
        // the figure anchor.
        const pcTransform = Matrix.concat( transform, parent.rwPowerClipTransform(), pcMatrix );
        pcContent.draw(context,pcTransform,config);
        context.restore();
      }
    }

    if( penAfterFill && !config.ignorePen ) {
      if( clipped ) {
        // It seems that even if you do a context.save() once you perform a context.clip()
        // things are screwed for the used path, and it needs to be redrawn
        drawPath(context,matrix);
      }
      this.drawStroke(context,transform,config,matrix,pen);
    }

    const topFigure = this.topFigure;
    if( topFigure && topFigure._highlight ) {
      const brush = recolorized( this.rwBrush(), config );
      if( ! brush._empty ) {
        context.save();
        const color = brush instanceof Brush ? Color.highestContrast(brush.color) : Color.White();
        context.globalAlpha = 0.3;
        context.fillStyle = color.rgba;
        context.fill(this.windingRule);
        context.restore();
      }
    }
  },

  drawWireframe( drawPath, context, transform, config, matrix ) {
    context.save();
    drawPath(context,matrix);
    strokeWireframe(context);
    context.restore();
  },

  drawCustom( drawPath, context, transform, config, matrix ) {
    context.save();
    drawPath(context,matrix);
    config.customDraw(context,transform,config);
    context.restore();
  },

  // fillMode CadX works exactly as alternate here
  clipContext( context, transform ) {
    const { regions } = this;
    const matrix = this.globalMatrix_(transform);
    context.beginPath();
    for(let k=0,kEnd=regions.length;k<kEnd;++k) {
      regions[k].draw(context,matrix);
    }
    context.clip( this.windingRule );
    context.beginPath();
  },

  drawForClipping( context, transform ) {
    const { regions } = this;
    const matrix = this.globalMatrix_(transform);
    for(let k=0,kEnd=regions.length;k<kEnd;++k) {
      regions[k].draw(context,matrix);
    }
  },
  

  draw( context, transform, config = {} ) {

    if( ! config.transformDoc ) {
      config = assign({ transformDoc: transform }, config );
    }

    const matrix = this.globalMatrix_(transform);

    // This is a little bit complex because when we have powerclips
    // we need to redraw the path for pen behind fill

    const drawFunction = (  config.showthroughMask ? 'drawShowthroughMask'  : 
                            config.wireframe       ? 'drawWireframe'        : 
                            config.showthroughColor && config.showthroughColor.alpha != 0.0 ? 'drawSolidShowthrough' :
                            config.customDraw      ? 'drawCustom'           : 
                                                     'drawStrokeAndFill'    );

    const regions = this._regions;
    if(this.hasHoles()) {
      //_.forEach(regions,function(r) {
      for(let k=0,kEnd=regions.length;k<kEnd;++k) {
        const region = regions[k];
        this[drawFunction]( (c,t) => {
          c.beginPath();
          region.draw(c,t);
        }, context,transform,config,matrix);
      }//,this);
    }
    else {
      this[drawFunction]( (/*c,t*/) => {
        context.beginPath();
        //_.forEach(regions,function(r) {
        for(let k=0,kEnd=regions.length;k<kEnd;++k) {
          regions[k].draw(context,matrix);
        }//);
      }, context,transform,config,matrix);
    }
  },

  globalMatrix_( transform ) {
    const parent = this._owner;
    if( parent && parent.globalMatrix_ ) {
      return Matrix.m( transform, parent.globalMatrix_() );
    }
    return transform;
  },

  // rwPen(): analogous to rwBrush() below.
  rwPen() {
    const parent = this._owner;
    if( this._pen ){
      if( parent && parent.pen ){ // the object and the parent have pens
        return this.combinedPen(parent._pen);
      }else{ // the parent doesn't exist or has no pen
        return this._pen;
      }
    }else{ // the object has no pen defined
      return parent._pen;
    }
  },

  // rwBrush() returns the brush used by this Polyregion. If the object and
  // the parent have brushes, it returns a combination of both. If only one
  // of the object and the parent has brush, it returns it. If noone has
  // brush, null is returned.
  rwBrush() {
    const parent = this._owner;
    if( this._brush ){
      if( parent && parent.brush ){ // the object and the parent have brushes
        return this.combinedBrush(parent._brush);
      }else{ // the parent doesn't exist or has no brush
        return this._brush;
      }
    }else{ // the object has no brush defined
      return parent._brush;
    }
  },

  combinedBrush( shapeBrush ) {
    return Brush.def(this._brush,shapeBrush);
  },
  combinedPen( shapePen ) {
    let pen = Pen.def(this._pen,shapePen) ;
    if( pen._type === 'Pen' && pen._width > 0.0 ) {
      // [TODO] push this inside pen.setupContext, avoid creating another pen
      const renderMatrixScaleFactor = this._renderMatrix ? this._renderMatrix.scaleFactor() : 1.0;
      pen = pen.clone();
      pen.width = pen._width * renderMatrixScaleFactor;
    }
    return pen ;
  },
  get topFigure() {
    const owner = this._owner;
    return owner && owner.topFigure;
  }
});

Polyregion.createFromBoundary = function( boundary, config ) {
  return new Polyregion([new Region([boundary])],config);
};

Polyregion.listFromXml = function( node ) {
  const polyregionNodes = Xml.children(node,'Polyregion');
  const polyregions = [];
  //_.forEach(polyregionNodes, function(pn) {
  for(let k=0,kEnd=polyregionNodes.length;k<kEnd;++k) {
    const pn = polyregionNodes[k];
    // Because of backward compatibility, Polyregion.fromXml can return
    // several polyregions. This happens when pens and brushes have been defined
    // at the region level, something that is currently deprecated.
    Polyregion.parseXml(pn, polyregions);
  }//);
  return polyregions;
};

// Because of backward compatibility, Polyregion.fromXml can return
// several polyregions. This happens when pens and brushes have been defined
// at the region level, something that is currently deprecated.
Polyregion.parseXml = function( node, polyregions = [] ) {

  const p = Xml.parseProperties(node,Polyregion);

  const buildPolyregion = function(regions,config ) {
    if(regions.length > 0) {
      polyregions.push(new Polyregion(regions,config));
    }
  };

  const regionNodes = Xml.children(node,'Region');
  let regions = [];
  //_.forEach(regionNodes, function(rn) {
  for(let k=0,kEnd=regionNodes.length;k<kEnd;++k) {
    const rn = regionNodes[k];
    // A region that have a pen or a brush can generate polyregions
    const region = Region.parseXml(rn, p, buildPolyregion);
    if(! region ) {
      buildPolyregion(regions,p);
      regions = [];
    }
    else {
      regions.push(region);
    }
  }//);

  buildPolyregion(regions,p);
};

Polyregion.CreateRectangle = function( config ) {
  const boundary = Boundary.createRectangle(config);
  const polyregion = Polyregion.createFromBoundary(boundary);
  if( config.brush ) {
    polyregion.brush = config.brush;
  }
  if( config.pen ) {
    polyregion.pen = config.pen;
  }
  return polyregion;
};

Polyregion.CreatePolyline = function( config ) {
  const boundary = new Polyline(config.points,config.closed||false);
  const polyregion = Polyregion.createFromBoundary(boundary);
  polyregion.pen = config.pen || Pen({width:1.0,color:Color({rgb:'000000'})});
  return polyregion;
};

Xml.Parser['Polyregion'  ] = Xml.makeObjectParser(Polyregion);
Xml.Parser['Polyregion[]'] = Xml.makeArrayObjectParser(Polyregion);



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/shape/polyregion.js
 **/